import {fragment} from '@xh/hoist/cmp/layout';
import {hoistCmp} from '@xh/hoist/core';
import {Icon} from '@xh/hoist/icon';
import {ReactElement} from 'react';
import {compactToolbarButton, compactToolbarMenuButton, toolbarButtonSeparator} from '../../../../cmp/toolbar/Toolbar';

interface ToolSpec {
    /** Highcharts navigationBindings class - e.g. 'highcharts-circle-annotation'. Also serves as the click target Highcharts' NavigationBindings delegates on. */
    className: string;
    title: string;
}

/**
 * Drawing-tools buttons for a stock chart's toolbar, driving Highcharts' `navigationBindings`
 * extension (registered by the `annotations-advanced`/`stock-tools` modules, see Bootstrap.ts) -
 * modeled on the tool set from Highcharts' "Stock chart with custom GUI" demo
 * (https://www.highcharts.com/demo/stock/stock-tools-custom-gui), reworked here as menu buttons to sit
 * inline in this widget's existing toolbar.
 *
 * NavigationBindings delegates clicks by inspecting the classList of the clicked element and its
 * ancestors up to its bound container (see StockChartWidgetModel.stockToolsBindingsClassName, set
 * as the toolbar's own className) - so each menu item below carries a Highcharts binding class
 * and is left otherwise unwired, with no `onClick` of our own.
 */
export const stockToolsToolbar = hoistCmp.factory({
    render() {
        return fragment(
            toolbarButtonSeparator(),
            toolGroup('Shapes', Icon.tag(), [
                {className: 'highcharts-label-annotation', title: 'Label'},
                {className: 'highcharts-circle-annotation', title: 'Circle'},
                {className: 'highcharts-rectangle-annotation', title: 'Rectangle'},
                {className: 'highcharts-ellipse-annotation', title: 'Ellipse'}
            ]),
            toolGroup('Lines', Icon.arrowRightArrowLeft(), [
                {className: 'highcharts-segment', title: 'Segment'},
                {className: 'highcharts-arrow-segment', title: 'Arrow segment'},
                {className: 'highcharts-ray', title: 'Ray'},
                {className: 'highcharts-arrow-ray', title: 'Arrow ray'},
                {className: 'highcharts-infinity-line', title: 'Line'},
                {className: 'highcharts-arrow-infinity-line', title: 'Arrow line'},
                {className: 'highcharts-horizontal-line', title: 'Horizontal line'},
                {className: 'highcharts-vertical-line', title: 'Vertical line'}
            ]),
            toolGroup('Crooked lines', Icon.chartLine(), [
                {className: 'highcharts-elliott3', title: 'Elliott 3'},
                {className: 'highcharts-elliott5', title: 'Elliott 5'},
                {className: 'highcharts-crooked3', title: 'Crooked 3'},
                {className: 'highcharts-crooked5', title: 'Crooked 5'}
            ]),
            toolGroup('Measure', Icon.crosshairs(), [
                {className: 'highcharts-measure-xy', title: 'Measure XY'},
                {className: 'highcharts-measure-x', title: 'Measure X'},
                {className: 'highcharts-measure-y', title: 'Measure Y'}
            ]),
            toolGroup('Advanced', Icon.sigma(), [
                {className: 'highcharts-fibonacci', title: 'Fibonacci'},
                {className: 'highcharts-pitchfork', title: 'Pitchfork'},
                {className: 'highcharts-parallel-channel', title: 'Parallel channel'}
            ]),
            toolGroup('Vertical labels', Icon.tags(), [
                {className: 'highcharts-vertical-counter', title: 'Counter'},
                {className: 'highcharts-vertical-label', title: 'Label'},
                {className: 'highcharts-vertical-arrow', title: 'Arrow'}
            ]),
            toolGroup('Flags', Icon.flag(), [
                {className: 'highcharts-flag-circlepin', title: 'Circle'},
                {className: 'highcharts-flag-diamondpin', title: 'Diamond'},
                {className: 'highcharts-flag-squarepin', title: 'Square'},
                {className: 'highcharts-flag-simplepin', title: 'Simple'}
            ]),
            compactToolbarButton({
                className: 'highcharts-toggle-annotations',
                icon: Icon.eye(),
                tooltip: 'Toggle annotations'
            }),
            toolbarButtonSeparator()
        );
    }
});

function toolGroup(title: string, icon: ReactElement, leaves: ToolSpec[]) {
    return fragment(
        compactToolbarMenuButton({
            icon,
            tooltip: title,
            // Must stay out of a portal so its menu items remain DOM descendants of the toolbar -
            // Highcharts' NavigationBindings delegates clicks via a native listener on that specific
            // element, which a portal (rendered elsewhere in the DOM) would never bubble through.
            // `positioningStrategy: 'fixed'` keeps the menu from being clipped by the panel's own
            // overflow, without moving it out of the DOM subtree the way a portal would.
            popoverProps: {usePortal: false, positioningStrategy: 'fixed'},
            menuItems: leaves.map(leaf => ({className: leaf.className, icon, text: leaf.title}))
        }),
        toolbarButtonSeparator()
    );
}
